import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Confirmation from "../../common/components/TaskConfirmation.js";
import * as APIService from "../../common/api/index.js";
import URL from "../../common/api/ConstantURL";
import { getSessionContext } from "../../common/utils/sessionContext";
import AlertMessage from "../../common/components/AlertMessage";
import Backdrop from "@mui/material/Backdrop";
import { IoChevronBackCircleSharp } from "react-icons/io5";
import { LuNotepadText } from "react-icons/lu";
import { BsChatLeftText, BsThreeDots } from "react-icons/bs";
import ThumbDownAltRoundedIcon from "@mui/icons-material/ThumbDownAltRounded";
import NoAccountsIcon from "@mui/icons-material/NoAccounts";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AddTaskIcon from "@mui/icons-material/AddTask";
import { FaCheckSquare } from "react-icons/fa";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import * as Sentry from "@sentry/react";

const ResidentTask = () => {
  const [value, setValue] = useState(0);
  const [showTodoPopup, setShowTodoPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [alertStatus, setAlertStatusBase] = useState("");
  const [unacknowledgedClientChecklist, setUnacknowledgedClientChecklist] = useState([]);
  const [acknowledgedClientChecklist, setAcknowledgedClientChecklist] = useState([]);
  const [unacknowledgedExtraTasks, setUnacknowledgedExtraTasks] = useState([]);
  const [acknowledgedExtraTasks, setAcknowledgedExtraTasks] = useState([]);
  const [clientChecklistId, setClientChecklistId] = useState(0);
  const [clientChecklistTaskType, setClientChecklistTaskType] = useState("");
  const [clientChecklistRow, setClientChecklistRow] = useState("");
  const [taskStatusConfigList, setTaskStatusConfigList] = useState([]);
  const [fabMenuOpen, setFabMenuOpen] = useState(false);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverReason, setPopoverReason] = useState("");
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  useEffect(() => {
    getStatusConfigurationList();
    loadTabData(0);
  }, []);

  const getStatusConfigurationList = () => {
    APIService.getData(URL.getStatusConfigList)
      .then((responseData) => {
        if (responseData.code == 200) {
          const sortedData = responseData.data.sort((a, b) => a.displayOrder - b.displayOrder);
          setTaskStatusConfigList(sortedData);
        } else {
          setAlertStatusBase({ message: responseData.message, status: responseData.code, origin: "center" });
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        setAlertStatusBase({ message: "Error while fetch unacknowledged checklist of caregiver", status: "400", origin: "center" });
      });
  };

  const normalizeChecklist = (data) => {
    const list = Array.isArray(data) ? data : [];
    const withDisplayOrder = list.filter((task) => task.displayOrder && task.displayOrder !== 0).sort((a, b) => a.displayOrder - b.displayOrder);
    const withoutDisplayOrder = list.filter((task) => !task.displayOrder || task.displayOrder === 0);
    return [...withDisplayOrder, ...withoutDisplayOrder];
  };

  const loadTabData = (tabValue) => {
    const { residentId } = getSessionContext();
    const clientId = residentId;
    if (!clientId) {
      setUnacknowledgedClientChecklist([]);
      setAcknowledgedClientChecklist([]);
      setUnacknowledgedExtraTasks([]);
      setAcknowledgedExtraTasks([]);
      setDataLoaded(true);
      return;
    }

    setDataLoaded(false);
    setLoading(true);

    const checklistReq = tabValue == 0 ? APIService.getData(URL.getAllUnackChecklistOfClient + "?clientId=" + clientId) : APIService.getData(URL.getAllAckChecklistOfClient + "?clientId=" + clientId);
    const extraReq = tabValue == 0 ? APIService.getData(URL.getUnAckAdditionalTasks + "?clientId=" + clientId) : APIService.getData(URL.getAckAdditionalTasks + "?clientId=" + clientId);

    Promise.allSettled([checklistReq, extraReq])
      .then(([checklistRes, extraRes]) => {
        const errors = [];
        let checklistFailed = false;
        let extraFailed = false;

        if (checklistRes.status === "fulfilled") {
          const resp = checklistRes.value;
          if (resp?.code == 200) {
            if (tabValue == 0) setUnacknowledgedClientChecklist(normalizeChecklist(resp.data));
            else setAcknowledgedClientChecklist(Array.isArray(resp.data) ? resp.data : []);
          } else {
            checklistFailed = true;
            errors.push(resp?.message || "Failed to fetch checklist tasks");
            if (tabValue == 0) setUnacknowledgedClientChecklist([]);
            else setAcknowledgedClientChecklist([]);
          }
        } else {
          checklistFailed = true;
          errors.push("Error while fetching checklist tasks");
          if (tabValue == 0) setUnacknowledgedClientChecklist([]);
          else setAcknowledgedClientChecklist([]);
        }

        if (extraRes.status === "fulfilled") {
          const resp = extraRes.value;
          if (resp?.status === "success") {
            const list = Array.isArray(resp.data) ? resp.data : [];
            if (tabValue == 0) setUnacknowledgedExtraTasks(list);
            else setAcknowledgedExtraTasks(list);
          } else {
            extraFailed = true;
            errors.push(resp?.message || "Failed to fetch extra tasks");
            if (tabValue == 0) setUnacknowledgedExtraTasks([]);
            else setAcknowledgedExtraTasks([]);
          }
        } else {
          extraFailed = true;
          errors.push("Error while fetching extra tasks");
          if (tabValue == 0) setUnacknowledgedExtraTasks([]);
          else setAcknowledgedExtraTasks([]);
        }

        if (checklistFailed && extraFailed) {
          setAlertStatusBase({ message: errors[0] || "Failed to fetch tasks", status: "400", origin: "center" });
        } else if (errors.length > 0) {
          setAlertStatusBase({ message: errors[0], status: "200", origin: "center" });
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        setUnacknowledgedClientChecklist([]);
        setAcknowledgedClientChecklist([]);
        setUnacknowledgedExtraTasks([]);
        setAcknowledgedExtraTasks([]);
        setAlertStatusBase({ message: "Error while fetching tasks", status: "400", origin: "center" });
      })
      .finally(() => {
        setLoading(false);
        setDataLoaded(true);
      });
  };

  const refreshAdditionalTasksOnly = (tabValue) => {
    const { residentId } = getSessionContext();
    const clientId = residentId;
    if (!clientId) {
      if (tabValue == 0) setUnacknowledgedExtraTasks([]);
      else setAcknowledgedExtraTasks([]);
      return;
    }
    const extraReq = tabValue == 0 ? APIService.getData(URL.getUnAckAdditionalTasks + "?clientId=" + clientId) : APIService.getData(URL.getAckAdditionalTasks + "?clientId=" + clientId);
    extraReq
      .then((resp) => {
        if (resp?.status === "success") {
          const list = Array.isArray(resp.data) ? resp.data : [];
          if (tabValue == 0) setUnacknowledgedExtraTasks(list);
          else setAcknowledgedExtraTasks(list);
        } else {
          if (tabValue == 0) setUnacknowledgedExtraTasks([]);
          else setAcknowledgedExtraTasks([]);
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        if (tabValue == 0) setUnacknowledgedExtraTasks([]);
        else setAcknowledgedExtraTasks([]);
      });
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
    getStatusConfigurationList();
    loadTabData(newValue);
  };

  const openTodo = (e, checklist) => {
    setClientChecklistRow(checklist);
    setClientChecklistId(checklist.checklistId);
    setClientChecklistTaskType(checklist.taskType);
    setShowTodoPopup(true);
  };

  const closeTodoConfirmation = () => {
    setClientChecklistId(0);
    setClientChecklistRow("");
    setShowTodoPopup(false);
  };

  const confirmationCallback = (data) => {
    if (clientChecklistTaskType === "EXTRA") {
      updateAdditionalTaskStatus(data);
      return;
    }
    updateClientChecklist(data);
  };

  const updateAdditionalTaskStatus = (data) => {
    const updateAdditionalTaskPayload = {
      reason: data?.reasonValue || "",
      checklistId: clientChecklistId,
      acknowledgeFlag: Number(data?.statusId),
    };
    setLoading(true);
    APIService.postJsonData(URL.updateAdditionalTaskStatus, updateAdditionalTaskPayload)
      .then((responseData) => {
        setLoading(false);
        if (responseData?.status === "success") {
          closeTodoConfirmation();
          setAlertStatusBase({ message: responseData.message || "Task status updated successfully", status: "200", origin: "center" });
          refreshAdditionalTasksOnly(value);
        } else {
          setAlertStatusBase({ message: responseData?.message || "Failed to update task status", status: "400", origin: "center" });
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        setLoading(false);
        setAlertStatusBase({ message: "Failed to update task status", status: "400", origin: "center" });
      });
  };

  const updateClientChecklist = (data) => {
    setLoading(true);
    const { caregiverId, residentId } = getSessionContext();
    var requestData = { caregiverId, clientId: residentId, checklistId: clientChecklistId, taskType: clientChecklistTaskType, checklistResponseFlag: data.statusId, reason: data.reasonValue };
    APIService.postData(URL.upateResponseOfClientChecklist, requestData)
      .then((responseData) => {
        // Handle response data
        setLoading(false);
        if (responseData.code == 200) {
          closeTodoConfirmation();
          setAlertStatusBase({ message: responseData.message, status: responseData.code, origin: "center" });
          loadTabData(value);
        } else {
          setAlertStatusBase({ message: responseData.message, status: responseData.code, origin: "center" });
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        setLoading(false);
        setAlertStatusBase({ message: "Error while acknowledge caregiver checklist", status: "400", origin: "center" });
      });
  };

  const closeAlert = () => setAlertStatusBase("");
  const handleBack = () => navigate(-1);
  const openAddNote = () => navigate("/additionals");
  const openNotes = () => navigate("/notes");
  const toggleFabMenu = () => setFabMenuOpen((prev) => !prev);
  const closeActionSheet = () => setFabMenuOpen(false);
  const handleSheetAddTask = () => {
    closeActionSheet();
    openAddNote();
  };
  const handleSheetNotes = () => {
    closeActionSheet();
    openNotes();
  };

  const handleClick = (event, reason) => {
    const normalizedReason = typeof reason === "string" ? reason.trim() : "";
    if (normalizedReason) {
      setPopoverReason(normalizedReason);
      setAnchorEl(event.currentTarget);
    } else {
      setPopoverReason("");
      setAnchorEl(null);
    }
  };
  const handleClose = () => {
    setPopoverReason("");
    setAnchorEl(null);
  };

  return (
    <>
      <Header />
      <div className="home-container">
        <Backdrop sx={{ color: "#ffba30", zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={loading}>
          <img src={require("../../assets/btTree.png")} className="loading-img" />
        </Backdrop>
        {alertStatus ? <AlertMessage status={alertStatus.status} message={alertStatus.message} origin={alertStatus.origin} closeAlert={closeAlert} /> : null}
        <div className="home-inner-container resident-task-layout" style={{ position: "relative", width: "99%" }}>
          <div className="heading vitals-heading">
            <IoChevronBackCircleSharp className="vitals-preview-header" onClick={handleBack} />
            <span style={{ fontWeight: "500", paddingLeft: "0px" }}>{getSessionContext().residentName}</span>
            <button
              type="button"
              onClick={openNotes}
              style={{
                border: "1px solid #dd8f1d",
                backgroundColor: "#dd8f1d",
                color: "#ffffff",
                borderRadius: "10px",
                padding: "6px 12px",
                fontSize: "12px",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                <BsChatLeftText />
                <span>Notes</span>
              </span>
            </button>
          </div>
          <Box className="tab col-11 col-sm-11 col-md-8 col-lg-4 col-xl-4">
            <Tabs
              sx={{
                minHeight: 32,
                "& .MuiTabs-flexContainer": { height: "100%" },
                "& .MuiTab-root": { fontSize: "clamp(15px,1.5vw,16px)", height: "100%", paddingTop: "9px", paddingBottom: "9px" },
              }}
              onChange={handleChange}
              value={value}
              aria-label="Tabs where selection follows focus"
              selectionFollowsFocus>
              <Tab value={0} label="TO-DO" />
              <Tab value={1} label="COMPLETED" />
            </Tabs>
          </Box>
          <div className="task-container scrollbar-orange col-11 col-sm-11 col-md-8 col-lg-4 col-xl-4">
            {!loading &&
              dataLoaded &&
              (value == 0 ? (
                <>
                  {unacknowledgedExtraTasks.length > 0 && (
                    <label className="task-section-label" htmlFor="">
                      <AssignmentIcon className="task-section-icon" />
                      <span>Assigned Tasks</span>
                    </label>
                  )}
                  <div className={unacknowledgedExtraTasks.length > 0 ? "assigned-sec" : "assigned-sec assigned-sec-full"}>
                    {unacknowledgedClientChecklist.length > 0 ? (
                      <SimpleBar className="task-section-simplebar" autoHide={false}>
                        {unacknowledgedClientChecklist.map((checkList, index) => (
                          <div key={checkList.checklistId || index} style={{ justifyContent: "space-between" }} className="task-item">
                            <span onClick={(e) => openTodo(e, checkList)} style={{ width: "90%" }} className="task-title">
                              {checkList.title}
                            </span>
                            {checkList.statusId == 1 ? (
                              <FaCheckSquare style={{ color: "rgb(43, 183, 43)" }} onClick={(e) => handleClick(e, checkList.reason)} />
                            ) : checkList.statusId == 2 ? (
                              <NoAccountsIcon style={{ color: "#ea3c3c" }} onClick={(e) => handleClick(e, checkList.reason)} />
                            ) : checkList.statusId == 3 ? (
                              <ThumbDownAltRoundedIcon style={{ color: "#ea3c3c" }} onClick={(e) => handleClick(e, checkList.reason)} />
                            ) : null}
                          </div>
                        ))}
                      </SimpleBar>
                    ) : (
                      <div className="no-task-section">
                        <span>No Task Available</span>
                      </div>
                    )}
                  </div>
                  {unacknowledgedExtraTasks.length > 0 && (
                    <>
                      <label className="task-section-label" id="label-extra-task" htmlFor="">
                        <AddTaskIcon className="task-section-icon" />
                        <span>New Tasks</span>
                      </label>
                      <div className="extra-sec">
                        <SimpleBar className="task-section-simplebar" autoHide={false}>
                          {unacknowledgedExtraTasks.map((task, index) => (
                            <div key={`extra-todo-${task.checklistId || index}`} style={{ justifyContent: "space-between" }} className="task-item task-item-additional">
                              <span
                                style={{ width: "90%" }}
                                className="task-title"
                                onClick={(e) =>
                                  openTodo(e, {
                                    checklistId: task.checklistId,
                                    taskType: "EXTRA",
                                    title: task.title,
                                    description: task.description,
                                    statusId: task.acknowledgeFlag,
                                    reason: task.reason,
                                  })
                                }>
                                {task.title}
                              </span>
                              {task.acknowledgeFlag == 1 || task.acknowledgeFlag == 2 ? (
                                <FaCheckSquare style={{ color: "rgb(43, 183, 43)" }} onClick={(e) => handleClick(e, task.reason)} />
                              ) : task.acknowledgeFlag == 3 ? (
                                <ThumbDownAltRoundedIcon style={{ color: "#ea3c3c" }} onClick={(e) => handleClick(e, task.reason)} />
                              ) : null}
                            </div>
                          ))}
                        </SimpleBar>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <>
                  {acknowledgedExtraTasks.length > 0 && (
                    <label className="task-section-label" htmlFor="">
                      <AssignmentIcon className="task-section-icon" />
                      <span>Assigned Tasks</span>
                    </label>
                  )}
                  <div className={acknowledgedExtraTasks.length > 0 ? "assigned-sec" : "assigned-sec assigned-sec-full"}>
                    {acknowledgedClientChecklist.length > 0 ? (
                      <SimpleBar className="task-section-simplebar" autoHide={false}>
                        {acknowledgedClientChecklist.map((checkList, index) => (
                          <div key={checkList.checklistId || index} style={{ justifyContent: "space-between" }} className="task-item">
                            <span onClick={(e) => openTodo(e, checkList)} style={{ width: "90%" }} className="task-title">
                              {checkList.title}
                            </span>
                            {checkList.statusId == 1 ? (
                              <FaCheckSquare style={{ color: "rgb(43, 183, 43)" }} onClick={(e) => handleClick(e, checkList.reason)} />
                            ) : checkList.statusId == 2 ? (
                              <NoAccountsIcon style={{ color: "#ea3c3c" }} onClick={(e) => handleClick(e, checkList.reason)} />
                            ) : checkList.statusId == 3 ? (
                              <ThumbDownAltRoundedIcon style={{ color: "#ea3c3c" }} onClick={(e) => handleClick(e, checkList.reason)} />
                            ) : null}
                          </div>
                        ))}
                      </SimpleBar>
                    ) : (
                      <div className="no-task-section">
                        <span>No Task Available</span>
                      </div>
                    )}
                  </div>
                  {acknowledgedExtraTasks.length > 0 && (
                    <>
                      <label className="task-section-label" id="label-extra-task" htmlFor="">
                        <AddTaskIcon className="task-section-icon" />
                        <span>New Tasks</span>
                      </label>
                      <div className="extra-sec">
                        <SimpleBar className="task-section-simplebar" autoHide={false}>
                          {acknowledgedExtraTasks.map((task, index) => (
                            <div key={`extra-ack-${task.checklistId || index}`} style={{ justifyContent: "space-between" }} className="task-item task-item-additional">
                              <span
                                style={{ width: "90%" }}
                                className="task-title"
                                onClick={(e) =>
                                  openTodo(e, {
                                    checklistId: task.checklistId,
                                    taskType: "EXTRA",
                                    title: task.title,
                                    description: task.description,
                                    statusId: task.acknowledgeFlag,
                                    reason: task.reason,
                                  })
                                }>
                                {task.title}
                              </span>
                              {task.acknowledgeFlag == 1 || task.acknowledgeFlag == 2 ? (
                                <FaCheckSquare style={{ color: "rgb(43, 183, 43)" }} onClick={(e) => handleClick(e, task.reason)} />
                              ) : task.acknowledgeFlag == 3 ? (
                                <ThumbDownAltRoundedIcon style={{ color: "#ea3c3c" }} onClick={(e) => handleClick(e, task.reason)} />
                              ) : null}
                            </div>
                          ))}
                        </SimpleBar>
                      </div>
                    </>
                  )}
                </>
              ))}
            <Popover id={id} open={open} anchorEl={anchorEl} onClose={handleClose} anchorOrigin={{ vertical: "bottom", horizontal: "left" }}>
              <Typography sx={{ p: 2 }}>{popoverReason}</Typography>
            </Popover>
            <Confirmation row={clientChecklistRow} open={showTodoPopup} closeConfirmation={(data) => closeTodoConfirmation(data)} processCallback={(data) => confirmationCallback(data)} taskStatusConfigList={taskStatusConfigList} />
          </div>
          {fabMenuOpen ? (
            <div className="resident-task-action-sheet" role="dialog" aria-modal="true" aria-label="Task actions">
              <button type="button" className="resident-task-action-sheet-backdrop" onClick={closeActionSheet} aria-label="Close" />
              <div className="resident-task-action-sheet-panel">
                <div className="resident-task-action-sheet-card">
                  <div className="resident-task-action-sheet-handle" aria-hidden="true" />
                  <div className="resident-task-action-sheet-header">QUICK ACTIONS</div>
                  <button type="button" className="resident-task-action-sheet-row" onClick={handleSheetAddTask}>
                    <span className="resident-task-action-sheet-icon-wrap">
                      <AddTaskIcon className="resident-task-action-sheet-row-icon" />
                    </span>
                    <span className="resident-task-action-sheet-copy">
                      <span className="resident-task-action-sheet-title">Add Request</span>
                      <span className="resident-task-action-sheet-subtitle">Add additional requests</span>
                    </span>
                  </button>
                  <div className="resident-task-action-sheet-divider" />
                  <button type="button" className="resident-task-action-sheet-row" onClick={handleSheetNotes}>
                    <span className="resident-task-action-sheet-icon-wrap">
                      <LuNotepadText className="resident-task-action-sheet-row-icon-svg" />
                    </span>
                    <span className="resident-task-action-sheet-copy">
                      <span className="resident-task-action-sheet-title">Manage Notes</span>
                      <span className="resident-task-action-sheet-subtitle">View & Add shift notes</span>
                    </span>
                  </button>
                  <div className="resident-task-action-sheet-cancel-wrap">
                    <button type="button" className="resident-task-action-sheet-cancel" onClick={closeActionSheet}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
          <div className="task-fab-row task-fab-stack task-fab-floating">
            <button className="task-extra-fab" onClick={toggleFabMenu} aria-label="Open task actions">
              <BsThreeDots className="task-extra-fab-icon" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ResidentTask;
